import React, { useEffect, useRef, useState } from 'react';
import { useTagColors } from '../hooks/useTagColors';
import TagMultiSelect from './TagMultiSelect';

// Picked tags: include = a listing with ANY of `tags`; exclude = hide a
// listing with ANY of `antiTags` (always replaced, never mutated)
export const EMPTY_FILTERS = {
  dateFrom: '',
  dateTo: '',
  performer: '',
  venue: '',
  tags: [],
  antiTags: [],
  listed: '',
  purchaseDateFrom: '',
  purchaseDateTo: ''
};

const NO_TAG_COLORS = {};

// Draft filter state lives here, not in Dashboard, so keystrokes re-render
// only this form and not the full event list (memo + stable callbacks).
// Filters auto-apply on a short debounce — there is no Search button; Enter
// applies immediately.
const DashboardFilters = React.memo(function DashboardFilters({ inventoryTags, onSearch, onClear }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const { data: tagColors = NO_TAG_COLORS } = useTagColors();
  const debounceRef = useRef(null);
  const skipNextApply = useRef(true); // don't fire on mount or right after Clear

  useEffect(() => {
    if (skipNextApply.current) {
      skipNextApply.current = false;
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearch(filters), 400);
    return () => clearTimeout(debounceRef.current);
  }, [filters, onSearch]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Enter applies immediately instead of waiting out the debounce
  const handleSubmit = (e) => {
    e.preventDefault();
    clearTimeout(debounceRef.current);
    onSearch(filters);
  };

  const handleClear = () => {
    clearTimeout(debounceRef.current);
    skipNextApply.current = true;
    setFilters(EMPTY_FILTERS);
    onClear();
  };

  return (
    <div className="card filters-rail">
      <form onSubmit={handleSubmit}>
        <div className="filters-rail-header">
          <span>Filters</span>
          <button type="button" className="link-btn" onClick={handleClear}>Clear all</button>
        </div>
        <div className="filter-group">
          <label>Event Date</label>
          <div className="filter-pair">
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="filter-input"
            />
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="filter-input"
            />
          </div>
        </div>
        <div className="filter-group">
          <label>Artist/Event</label>
          <input
            type="text"
            value={filters.performer}
            onChange={(e) => handleFilterChange('performer', e.target.value)}
            placeholder="Search artist..."
            className="filter-input"
          />
        </div>
        <div className="filter-group">
          <label>Venue</label>
          <input
            type="text"
            value={filters.venue}
            onChange={(e) => handleFilterChange('venue', e.target.value)}
            placeholder="Search venue..."
            className="filter-input"
          />
        </div>
        <div className="filter-group">
          <label>Tag</label>
          <TagMultiSelect
            label="Tag"
            options={inventoryTags}
            selected={filters.tags}
            onChange={(next) => handleFilterChange('tags', next)}
            tagColors={tagColors}
            placeholder="Has any tag..."
          />
        </div>
        <div className="filter-group">
          <label>Exclude Tag</label>
          <TagMultiSelect
            label="Exclude Tag"
            options={inventoryTags}
            selected={filters.antiTags}
            onChange={(next) => handleFilterChange('antiTags', next)}
            tagColors={tagColors}
            placeholder="Hide any tag..."
          />
        </div>
        <div className="filter-group">
          <label>Listed</label>
          <select
            value={filters.listed}
            onChange={(e) => handleFilterChange('listed', e.target.value)}
            className="filter-input"
          >
            <option value="">All</option>
            <option value="listed">Listed</option>
            <option value="unlisted">Unlisted</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Purchased</label>
          <div className="filter-pair">
            <input
              type="date"
              value={filters.purchaseDateFrom}
              onChange={(e) => handleFilterChange('purchaseDateFrom', e.target.value)}
              className="filter-input"
            />
            <input
              type="date"
              value={filters.purchaseDateTo}
              onChange={(e) => handleFilterChange('purchaseDateTo', e.target.value)}
              className="filter-input"
            />
          </div>
        </div>
        <div className="filters-rail-note">Filters apply as you type.</div>
        <button type="submit" style={{ display: 'none' }} aria-hidden="true"></button>
      </form>
    </div>
  );
});

export default DashboardFilters;
